import 'css/src/components/public-pages/HomePage';

import React, { useState } from 'react';
import classNames from 'classnames';

import HomeTextSection from './sections/HomeTextSection';
import TemplatePreviewsSection from './TemplatePreviews';

import { Urls } from 'js/src/client';
import JumboCta from 'js/src/components/common/JumboCta';
import LogoImages from 'js/src/components/common/LogoImages';
import { BUTTON_SIZES, PrimaryButton, SecondaryButton } from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import CompanyInfoSection from 'js/src/components/public-pages/common-sections/CompanyInfoSection';
import VideoPlayer from 'js/src/components/public-pages/common-sections/VideoPlayer';
import VideoPlayerWithButton from 'js/src/components/public-pages/common-sections/VideoPlayerWithButton';
import type { HomepageVideoDetails } from 'js/src/components/public-pages/homepage-data';
import {
    getDefaultTagId,
    getTagIds,
    getTagNameById,
    getVideosByTagId,
    logoImagesDark,
} from 'js/src/components/public-pages/homepage-data';
import ProfessionalVideosSection from 'js/src/components/public-pages/sections/ProfessionalVideosSection';

type HompageTagsProps = {
    tags: string[];
    activeTagId: string;
    changeTagId: (id: string) => void;
};
const HomepageTags = React.memo(function HomepageTags({
    tags,
    activeTagId,
    changeTagId,
}: HompageTagsProps) {
    const handleChangeTag = (id: string) => changeTagId(id);

    return (
        <div className="filter-and-search d-flex justify-content-around">
            {tags.map((id, idx) => (
                <div className="search-options" key={`${id}-${idx}`}>
                    <button
                        className={classNames('px-l5-16 py-l5-8', {
                            active: activeTagId === id,
                        })}
                        onClick={() => handleChangeTag(id)}
                    >
                        {getTagNameById(id)}
                    </button>
                </div>
            ))}
        </div>
    );
});

const HomePage = React.memo(function HomePage() {
    const [showDemoVideo, setShowDemoVideo] = useState(false);
    const [demoVideoDetails, setDemoVideoDetails] = useState<HomepageVideoDetails | null>(null);
    const [currentFilterId, setCurrentFilterId] = useState(getDefaultTagId());
    const tagIds = getTagIds();

    const playDemoVideoInModal = (demoVideoDetails: HomepageVideoDetails) => {
        setDemoVideoDetails(demoVideoDetails);
        setShowDemoVideo(true);
    };

    return (
        <div className="home-page-container mt-l5-0 overflow-x-hidden">
            {/* Section 1 Grow your brand */}
            <div className="p-l5-16 p-l5-sm-48 section-1 section-grey text-center pt-l5-0">
                <img
                    className="section-1-bg"
                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/Gradient%20Backgrounds-01-1280.webp"
                    srcSet="
                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/Gradient%20Backgrounds-01-320.webp   320w,
                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/Gradient%20Backgrounds-01-640.webp   640w,
                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/Gradient%20Backgrounds-01-960.webp   960w,
                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/Gradient%20Backgrounds-01-1280.webp 1280w
                    "
                    alt="Background gradient"
                    loading="eager"
                    // @ts-expect-error React 18 has a bug not recognizing fetchPriority: https://github.com/facebook/react/issues/28948
                    // eslint-disable-next-line react/no-unknown-property
                    fetchpriority="high"
                />
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        {/* section 1.1 */}
                        <div className="col-xl-6 col-12">
                            <div className="d-flex flex-column my-l5-48 pt-l5-48 text-center hero-title text-md-start">
                                <h1>
                                    AI video maker for business. Create on&#8209;brand videos
                                    from&nbsp;text.
                                </h1>
                                <div className="mt-l5-16 pt-l5-16 px-l5-0 hero-subtitle font-l5 font-l5--h4">
                                    Turn blogs, articles, scripts, and ideas into videos in minutes
                                    for marketing, communications, and education.
                                </div>
                                <div className="mt-l5-48">
                                    <PrimaryButton
                                        size={BUTTON_SIZES.LG}
                                        text="Sign up for free"
                                        href={Urls.SIGNUP}
                                        className="m-l5-8 hero-button-spacing"
                                    />
                                    <SecondaryButton
                                        size={BUTTON_SIZES.LG}
                                        text="Request a demo"
                                        href="https://lumen5.com/book-a-demo?utm_campaign=6880892-%5BMARKETING%5D%20Demo%20Request%20Form&utm_source=Website&utm_medium=homepage_request_a_demo"
                                        className="m-l5-8"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-6 col-12">
                            <div className="mt-l5-64 hero-image-container">
                                <img
                                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/HomeHeroStatic3-1280.webp"
                                    srcSet="
                                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/HomeHeroStatic3-320.webp   320w,
                                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/HomeHeroStatic3-640.webp   640w,
                                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/HomeHeroStatic3-960.webp   960w,
                                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/HomeHeroStatic3-1280.webp 1280w
                                    "
                                    className="w-100 p-l5-32 hero-image"
                                    alt="Lumen5"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2  Customer logos... */}
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        <div className="col-md-12">
                            <div className="d-flex py-l5-32">
                                <span className="fw-semibold logo-header font-l5 font-l5--h3">
                                    Trusted by global brands
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <LogoImages logos={logoImagesDark} lazyLoad={false} />
            </div>

            {/* Professional videos without the drama.... */}
            <ProfessionalVideosSection />

            {/* Turn anything into video... */}
            <HomeTextSection />

            <TemplatePreviewsSection />

            {/* Customizable templates */}
            <div className="p-l5-16 p-l5-sm-48 section-5">
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        {/* section 5.1 */}

                        <div className="col-md-6 col-sm-12 order-md-2 order-sm-1">
                            <div className="d-flex justify-content-center">
                                <div className="d-flex flex-column align-items-start my-l5-48">
                                    <div className="d-flex flex-column my-l5-16">
                                        <h2>
                                            Branded templates designed&nbsp;to help your team stay
                                            on brand,
                                            <br />
                                            on budget, and on time
                                        </h2>
                                    </div>
                                    <p className="font-l5 font-l5--regular my-l5-16 px-l5-0 w-75">
                                        Your brand, our Creative Services team. Bring your brand to
                                        life with a bespoke template ensuring all brand requirements
                                        are met, no matter the creator or asset.
                                    </p>
                                    <div className="font-l5 font-l5--regular my-l5-16 px-l5-0 w-75">
                                        <PrimaryButton
                                            size={BUTTON_SIZES.LG}
                                            text="Request a demo"
                                            href="https://lumen5.com/book-a-demo?utm_campaign=6880892-%5BMARKETING%5D%20Demo%20Request%20Form&utm_source=Website&utm_medium=homepage_request_a_demo"
                                            className="mt-l5-16"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* section 5.2 */}
                        <div className="col-md-6 col-sm-12 order-md-1 order-sm-2">
                            <div className="d-flex justify-content-sm-start justify-content-md-center align-items-center">
                                <video
                                    className="w-100 banner-img"
                                    autoPlay={true}
                                    muted
                                    loop
                                    playsInline
                                    disablePictureInPicture
                                    disableRemotePlayback
                                    preload="none"
                                >
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/choose-template.webm"
                                        type="video/webm"
                                    />
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/choose-template.mp4"
                                        type="video/mp4"
                                    />
                                </video>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 8 Lumen5 use cases */}
            <div className="p-l5-16 p-l5-sm-48 mb-l5-48 section-7">
                <div className="container">
                    {/* section 8.1 */}
                    <div className="row justify-content-center align-items-center">
                        <div className="col-md-12">
                            <div className="d-flex my-l5-16 justify-content-center text-center">
                                <h3>Explore videos made with Lumen5</h3>
                            </div>
                        </div>
                    </div>
                    {/* section 8.2 */}
                    <HomepageTags
                        tags={tagIds}
                        activeTagId={currentFilterId}
                        changeTagId={setCurrentFilterId}
                    />
                    <div className="row">
                        {getVideosByTagId(currentFilterId).map((video) => (
                            <VideoPlayerWithButton
                                key={`tagged-videos-${video.src}`}
                                className="col-md-4"
                                imgSrc={video.poster}
                                videoSrc={video.src}
                                onClick={() => playDemoVideoInModal(video)}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Section 9 CTA */}
            <div className="mb-l5-48">
                <JumboCta
                    header="Join over 4,000,000 companies using Lumen5 to tell their stories."
                    body="Lumen5 helps marketers, publishers, and brands create video content with AI, without the hassle or technical expertise."
                />
            </div>

            {/* Section 10 Case studies */}
            <div className="p-l5-16 p-l5-sm-48 section-7 testimonials-section">
                <img
                    className="testimonials-section__bg"
                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/testimonials_gradient.webp"
                    alt="background gradient"
                    loading="lazy"
                    decoding="async"
                />
                <div className="container">
                    {/* section 10.1 */}
                    <div className="row">
                        <div className="col-md-8 testimonials-section__text">
                            <div className="d-flex flex-column mb-l5-32 mt-l5-64">
                                <h2>
                                    From views to revenues:
                                    <br />
                                    See what video can really do
                                </h2>
                            </div>
                            <p className="font-l5 font-l5--regular my-l5-16 px-l5-0">
                                Video isn’t just for reach. It’s for results. Lumen5 helps you turn
                                passive views into real business outcomes with videos built for
                                engagement, conversion, and scale. Still curious? See how other
                                teams are using Lumen5 to turn attention into growth.
                            </p>
                        </div>
                    </div>
                    {/* section 10.2 */}
                    <CompanyInfoSection />
                </div>
            </div>

            {/* Section 11 Learning Resources */}
            <div className="p-l5-16 p-l5-sm-48 section-9">
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        <div className="col-md-6 col-sm-12 order-md-2 order-sm-1">
                            <div className="d-flex justify-content-center">
                                <div className="d-flex flex-column align-items-start my-l5-48">
                                    <div className="d-flex flex-column my-l5-16">
                                        <h3>The AI tool for video marketing</h3>
                                    </div>
                                    <div className="my-l5-16 px-l5-0 w-75 font-l5 font-l5--h5">
                                        Our mission is to help you become your brand&apos;s best
                                        storyteller.
                                    </div>
                                    <div className="font-l5 font-l5--regular my-l5-16 px-l5-0 w-75">
                                        <a href="/learn/" className="text-primary">
                                            Start learning for free →
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-sm-12 order-md-1 order-sm-2">
                            <div className="d-flex justify-content-sm-start justify-content-md-center align-items-center">
                                <video
                                    className="w-100 banner-img"
                                    autoPlay={true}
                                    muted
                                    loop
                                    playsInline
                                    disablePictureInPicture
                                    disableRemotePlayback
                                    preload="none"
                                >
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/learning-resources.webm"
                                        type="video/webm"
                                    />
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/learning-resources.mp4"
                                        type="video/mp4"
                                    />
                                </video>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Join the brands... */}
            <div className="my-l5-48 p-l5-16 p-l5-sm-48 mb-l5-48 lumen5-shape-background">
                <img
                    className="lumen5-shape-background-img"
                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/shapes/bottom_shapes.webp"
                    alt="background shapes"
                    loading="lazy"
                    decoding="async"
                />
                <div className="container align-items-start">
                    <div className="lumen5-shape-background-content">
                        <div className="d-flex flex-column align-items-start my-l5-16">
                            <h2>Join the brands using video to drive real growth</h2>
                        </div>
                        <p className="font-l5 font-l5--regular my-l5-16 px-l5-0">
                            Your content should do more than just get seen. With Lumen5, every video
                            helps move your audience forward and turns momentum into measurable
                            success.
                        </p>
                        <div className="d-flex align-items-center my-l5-48">
                            <PrimaryButton
                                size={BUTTON_SIZES.LG}
                                text="Request a demo"
                                href="https://lumen5.com/book-a-demo?utm_campaign=6880892-%5BMARKETING%5D%20Demo%20Request%20Form&utm_source=Website&utm_medium=homepage_request_a_demo"
                            />

                            <div className="font-l5 font-l5--regular my-l5-16 ms-l5-32">
                                <a href="/enterprise/" className="text-primary">
                                    Lumen5 for Enterprise →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Logo section */}
            <div className="p-l5-16">
                <LogoImages logos={logoImagesDark} rows={2} />
            </div>

            {/* Video Player Modal */}
            <Modal
                showing={showDemoVideo}
                onHidden={() => setShowDemoVideo(false)}
                closeable={false}
                size={demoVideoDetails ? demoVideoDetails.modalSize : MODAL_SIZES.XL}
                dialogClassName={'transparent-dialog text-center'}
                backdropClassName={'darker-backdrop'}
            >
                <div className="container-fluid">
                    {demoVideoDetails && (
                        <VideoPlayer
                            posterUrl={demoVideoDetails.poster}
                            videoUrl={demoVideoDetails.src}
                            showControls={true}
                            loop={false}
                            muted={false}
                            autoPlay={true}
                            classNames={`m-l5-4 rounded-video focus-outline-none
                                ${demoVideoDetails.aspectRatio === 'vertical' ? 'w-100' : 'w-100'}`}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
});

export default HomePage;
